import axios, { type AxiosInstance, type AxiosRequestConfig, type CreateAxiosDefaults } from 'axios';
import * as qs from 'qs';

import { objFilter, typeOf } from '../utils/object';

type BodyMethod = <T = any>(url: string, data?: unknown, config?: AxiosRequestConfig) => Promise<T>;
type BodylessMethod = <T = any>(url: string, config?: AxiosRequestConfig) => Promise<T>;

export interface DataClient extends Omit<AxiosInstance, 'request' | 'get' | 'delete' | 'head' | 'options' | 'post' | 'put' | 'patch' | 'postForm' | 'putForm' | 'patchForm'> {
  request<T = any>(config: AxiosRequestConfig): Promise<T>;
  get: BodylessMethod;
  delete: BodylessMethod;
  head: BodylessMethod;
  options: BodylessMethod;
  post: BodyMethod;
  put: BodyMethod;
  patch: BodyMethod;
  postForm: BodyMethod;
  putForm: BodyMethod;
  patchForm: BodyMethod;
}

interface InterceptorParams extends CreateAxiosDefaults {
  withStatus?: boolean;
  exposedHeaders?: string;
}

const createInterceptor = (initialParams: InterceptorParams, withExport = false): DataClient => {
  const axiosInstance = axios.create(initialParams);

  axiosInstance.defaults.paramsSerializer = {
    serialize: (params) => {
      const newParams = objFilter(params, (paramKey, paramValue) => (
        !(paramValue === '' || paramKey.endsWith('_aux'))
      ));
      return qs.stringify(newParams, { indices: false });
    },
    indexes: null,
  };

  axiosInstance.defaults.withCredentials = initialParams.withCredentials || false;
  axiosInstance.defaults.headers.common['Content-Type'] = 'application/json';
  axiosInstance.defaults.headers.common.Accept = 'application/json';

  axiosInstance.interceptors.request.use((config) => {
    if (config.data && config.data instanceof FormData) {
      config.headers['Content-Type'] = 'multipart/form-data';
      return config;
    }

    // Raw binary bodies (File/Blob/ArrayBuffer, e.g. octet-stream uploads) must
    // pass through untouched — objFilter would otherwise strip them to `{}`. The
    // caller keeps ownership of the Content-Type header in this case.
    if (config.data && (config.data instanceof Blob || config.data instanceof ArrayBuffer)) {
      return config;
    }

    let configData = config.data;

    if (config.data && typeOf(config.data) === 'object') {
      configData = objFilter(config.data, (dataKey, dataValue) => (
        !(dataValue === '' || dataKey.endsWith('_aux'))
      ));
    }

    return {
      ...config,
      data: configData,
    };
  }, (error) => Promise.reject(error));

  axiosInstance.interceptors.response.use((res) => {
    if (withExport || initialParams.withStatus) return res;
    return res.data;
  }, (error) => Promise.reject(error));

  return axiosInstance as unknown as DataClient;
};

export default createInterceptor;
